import transcriptImg from 'images/reports/reports_transcript.svg';
import codedTranscriptImg from 'images/reports/reports_codedtranscript.svg';
import codesDocImg from 'images/reports/reports_codebook.svg';
import codesCsvImg from 'images/reports/reports_codesascsv.svg';
import snippetsCsvImg from 'images/reports/reports_snippetsascsv.svg';
import demographicsCsvImg from 'images/reports/reports_demographics.svg';
import codeXCodeImg from 'images/reports/reports_codexcode.svg';
import codeXTranscriptImg from 'images/reports/reports_codextranscript.svg';
import codeXDescriptorImg from 'images/reports/reports_codesxdescriptor.svg';
import transcriptXDescriptorImg from 'images/reports/reports_transcriptxdescriptor.svg';
import codingsCsvImg from 'images/reports/reports_codingsascsv.svg';

export const REPORT_SECTIONS = [
  {
    title: 'Transcript exports',
    items: [
      {
        id: 'transcripts',
        name: 'Transcripts',
        description:
          "Downloads your transcripts as plain text files with no coding or highlights — just the raw text. Useful if you need to share transcripts with someone who doesn't use Delve, or import them into another tool.",
        buttonLabel: 'Export Transcript',
        includes: ['Transcript text'],
        kind: 'transcript-select',
        image: transcriptImg,
      },
      {
        id: 'coded-transcript',
        name: 'Coded Transcript',
        description:
          "Opens a printable view of your transcript with colored underlines showing where each code was applied, just like you see in Delve. Use your browser's print function to save as PDF.",
        buttonLabel: 'Export Coded Transcript',
        includes: ['Transcript text', 'In line code', 'Underlined snippets'],
        kind: 'coded-transcript-select',
        image: codedTranscriptImg,
      },
    ],
  },
  {
    title: 'Code exports',
    items: [
      {
        id: 'codes-doc',
        name: 'Codes as Doc',
        description: 'Downloads a Word document with every code in your project. Each code includes its description and all the snippets (quotes) you tagged with it, with links back to the original transcript.',
        buttonLabel: 'Export Codes as Doc',
        includes: ['Code names', 'Code descriptions', 'Snippets', 'Links to snippets'],
        image: codesDocImg,
      },
      {
        id: 'codes-csv',
        name: 'Codes as CSV',
        description: 'Downloads a spreadsheet with one row per code. Includes the code name, its nesting level, how many snippets it has, its description, and a link back to the snippet in Delve.',
        buttonLabel: 'Export Codes as CSV',
        includes: ['Code name', 'Nested level', 'Snippet count', 'Description'],
        image: codesCsvImg,
      },
    ],
  },
  {
    title: 'Snippet exports',
    items: [
      {
        id: 'snippets-csv',
        name: 'Snippets as CSV',
        description: 'Downloads a spreadsheet with one row per snippet. Includes the snippet, the transcript it came from, link to snippet in Delve, all codes applied to it, who coded it, any memos, and descriptor values.',
        buttonLabel: 'Export Snippets as CSV',
        includes: ['Snippet', 'Transcript source', 'Applied codes', 'Coded by', 'Memos', 'Descriptors'],
        hasFilter: true,
        image: snippetsCsvImg,
      },
      {
        id: 'codings-csv',
        name: 'Snippets as CSV (Long Format)',
        description:
          'This export is built for quantitative tools such as Excel PivotTables, R, and SPSS, which expect long-format data: every row is a single coding — one code, applied by one person, to one snippet. A snippet with three codes appears three times, with its transcript, descriptors, and memos repeated on each row, so you can count and cross-tabulate codes without reshaping the data first. Snippets with no codes are not included.',
        buttonLabel: 'Export Snippets as CSV (Long Format)',
        includes: ['One row per coding (one code, one coder)', 'When the coding was made (Coded At)', 'Snippet', 'Transcript source', 'Coded by', 'Memos', 'Descriptors'],
        hasFilter: true,
        image: codingsCsvImg,
      },
    ],
  },
  {
    title: 'Descriptor exports',
    items: [
      {
        id: 'demographics-csv',
        name: 'Descriptors as CSV',
        description:
          'Downloads a spreadsheet showing how many transcripts are in each descriptor option — for example, how many participants fall into each age group. Each descriptor also includes a row counting transcripts with no option selected.',
        buttonLabel: 'Export Descriptors as CSV',
        includes: ['Descriptor names', 'Descriptor options', 'Number of transcripts per option', 'No-selection counts'],
        image: demographicsCsvImg,
      },
      {
        id: 'transcript-x-descriptor',
        name: 'Transcript x Descriptor',
        description:
          'Downloads a spreadsheet with one row per transcript and one column per descriptor. Each cell shows the option selected for that transcript — an at-a-glance overview of participant information across your project.',
        buttonLabel: 'Export Transcript x Descriptor',
        includes: ['Transcripts as rows', 'Descriptors as columns', 'Selected option name in each cell'],
        image: transcriptXDescriptorImg,
      },
    ],
  },
  {
    title: 'Co-occurrence matrices',
    items: [
      {
        id: 'code-x-code',
        name: 'Code x Code Matrix',
        description: "Downloads a table showing how often any two codes appear on the same snippet. Use it to find which themes tend to co-occur \u2014 for example, do participants who mention \u2018frustration\u2019 also mention \u2018workarounds\u2019?",
        buttonLabel: 'Export Code x Code Matrix',
        includes: ['Codes as rows', 'Codes as columns', 'Count of each time two codes appear on the same snippet'],
        image: codeXCodeImg,
      },
      {
        id: 'code-x-transcript',
        name: 'Code x Transcript Matrix',
        description: 'Downloads a table showing how many times each code appears in each transcript.',
        buttonLabel: 'Export Code x Transcript Matrix',
        includes: ['Codes as rows', 'Transcripts as columns', 'Count of excerpts per transcript with this code'],
        image: codeXTranscriptImg,
      },
      {
        id: 'code-x-descriptor',
        name: 'Code x Descriptor Matrix',
        description:
          'Downloads a matrix of codes by descriptors. Each cell shows how many unique transcripts with that descriptor included a snippet with that code — useful for seeing if certain codes are concentrated in a particular group.',
        buttonLabel: 'Export Code x Descriptor Matrix',
        includes: [
          'Codes as rows',
          'Descriptors as columns',
          'Count of unique transcripts with this descriptor that have this code',
          'Total row with the number of transcripts that have each descriptor',
        ],
        image: codeXDescriptorImg,
      },
    ],
  },
];

export const ALL_REPORTS = REPORT_SECTIONS.flatMap((s) => s.items);
export const DEFAULT_REPORT_ID = 'codes-doc';
